/**
 * Email Service using SendGrid API
 * Handles all email communications for Fact Gather
 */

const SENDGRID_API_URL = 'https://api.sendgrid.com/v3/mail/send';

export interface EmailOptions {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  templateId?: string;
  dynamicTemplateData?: Record<string, any>;
}

export interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Send an email using SendGrid API via serverless function
 */
export async function sendEmail(options: EmailOptions): Promise<EmailResult> {
  try {
    const response = await fetch('/api/sendgrid/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send email');
    }

    const data = await response.json();
    return {
      success: true,
      messageId: data.messageId,
    };
  } catch (error) {
    console.error('[Email Service] Error sending email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Send interview link to client
 */
export async function sendInterviewLink(
  clientEmail: string,
  clientName: string,
  interviewUrl: string,
  appointmentDate: string
): Promise<EmailResult> {
  return sendEmail({
    to: clientEmail,
    subject: 'Lluis Law - Your Secure Interview Link',
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%); padding: 30px; text-align: center;">
          <h1 style="color: #f6e05e; margin: 0;">Lluis Law APC</h1>
          <p style="color: #e2e8f0; margin: 10px 0 0 0;">Secure Client Portal</p>
        </div>
        <div style="padding: 30px; background: #f7fafc;">
          <p>Dear ${clientName},</p>
          <p>Your consultation has been scheduled for <strong>${appointmentDate}</strong>.</p>
          <p>Please use the secure link below to begin your intake interview with Victoria, our AI intake specialist:</p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="${interviewUrl}" style="background: #d69e2e; color: #1a202c; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Begin Secure Interview</a>
          </div>
          <p style="color: #718096; font-size: 14px;">This link is unique to you and protected under attorney-client privilege.</p>
        </div>
        <div style="background: #2d3748; padding: 20px; text-align: center;">
          <p style="color: #a0aec0; font-size: 12px; margin: 0;">Lluis Law APC | Immigration & Family Law</p>
        </div>
      </div>
    `,
  });
}

/**
 * Send draft ready notification to attorney
 */
export async function sendDraftReadyNotification(
  attorneyEmail: string,
  clientName: string,
  caseType: string,
  draftUrl: string
): Promise<EmailResult> {
  return sendEmail({
    to: attorneyEmail,
    subject: `Draft Ready for Review: ${clientName} - ${caseType}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%); padding: 30px; text-align: center;">
          <h1 style="color: #f6e05e; margin: 0;">Fact Gather</h1>
          <p style="color: #e2e8f0; margin: 10px 0 0 0;">Draft Notification</p>
        </div>
        <div style="padding: 30px; background: #f7fafc;">
          <p>A new declaration draft is ready for your review:</p>
          <ul style="list-style: none; padding: 0;">
            <li><strong>Client:</strong> ${clientName}</li>
            <li><strong>Case Type:</strong> ${caseType}</li>
          </ul>
          <div style="text-align: center; margin: 30px 0;">
            <a href="${draftUrl}" style="background: #d69e2e; color: #1a202c; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Review Draft</a>
          </div>
        </div>
      </div>
    `,
  });
}

/**
 * Send appointment reminder email to client
 */
export async function sendReminderEmail(
  clientEmail: string,
  clientName: string,
  appointmentDate: string,
  timeLabel: string
): Promise<EmailResult> {
  return sendEmail({
    to: clientEmail,
    subject: `Lluis Law - Appointment Reminder (${timeLabel})`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%); padding: 30px; text-align: center;">
          <h1 style="color: #f6e05e; margin: 0;">Lluis Law APC</h1>
          <p style="color: #e2e8f0; margin: 10px 0 0 0;">Appointment Reminder</p>
        </div>
        <div style="padding: 30px; background: #f7fafc;">
          <p>Dear ${clientName},</p>
          <p>This is a reminder that your consultation is scheduled for <strong>${appointmentDate}</strong>.</p>
          <p>Your intake starts in <strong>${timeLabel}</strong>.</p>
          <p style="color: #718096; font-size: 14px;">If you need to reschedule, please reply to this email or contact our office.</p>
        </div>
        <div style="background: #2d3748; padding: 20px; text-align: center;">
          <p style="color: #a0aec0; font-size: 12px; margin: 0;">Lluis Law APC | Immigration & Family Law</p>
        </div>
      </div>
    `,
  });
}

export const EmailService = {
  sendEmail,
  sendInterviewLink,
  sendDraftReadyNotification,
    sendReminderEmail,
};
